import React, { useState, useEffect, useMemo } from "react";
import GradientBorderCard from "./GradientBorderCard";
import {
  CheckIcon,
  XCircleIcon,
  CloudArrowDownIcon,
  DatabaseIcon,
  InfinityIcon,
  SpinnerIcon,
} from "@phosphor-icons/react";
import NumberFlow, { type Format } from "@number-flow/react";
import { cn } from "../../../lib/utils";
import { IStripePlan } from "../../../interfaces";
import { useTheme } from "../../../providers/ThemeProvider";
import { ICardTheme } from "../interface/pricingMeta";

interface Props {
  plan: IStripePlan;
  theme: ICardTheme;
  isAnnual: boolean;
  canStartTrial: boolean;
  hasActivePlan: boolean;
  onSubscribe: (planId: number) => void;
  isPending: boolean;
  pendingPlanId: number | null;
  currentPlanAmount: number | null;
}

const AnimatedPrice: React.FC<{
  value: number;
  format: Format;
  locales?: string;
  className?: string;
}> = ({ value, format, locales = "en-US", className }) => {
  const [displayVal, setDisplayVal] = useState(0);

  useEffect(() => {
    setDisplayVal(value);
  }, [value]);

  return (
    <NumberFlow
      value={displayVal}
      format={format}
      locales={locales}
      className={className}
    />
  );
};

const formatDays = (days: number): string => {
  if (days >= 365) return `${Math.round(days / 365)} yr`;
  if (days >= 30) return `${Math.round(days / 30)} mo`;
  return `${days} days`;
};

const PricingCard: React.FC<Props> = ({
  plan,
  theme,
  isAnnual,
  canStartTrial,
  hasActivePlan,
  onSubscribe,
  isPending,
  pendingPlanId,
  currentPlanAmount,
}) => {
  const pkg = plan.package;

  // Resolve metadata from nested package (new API) with fallbacks to flat fields (old API)
  const isEnterprise = !!(pkg ? pkg.is_enterprise : plan.is_enterprise);
  const maxApps = pkg !== undefined ? pkg.max_apps : plan.max_apps;
  const initialDataPullDays = pkg !== undefined ? pkg.initial_data_pull_days : plan.initial_data_pull_days;
  const liveDashboardWindowDays = pkg !== undefined ? pkg.live_dashboard_window_days : plan.live_dashboard_window_days;

  // Group features by category for structured display
  const allFeatureDetails = pkg?.features_detail ?? [];
  const featuresByCategory = allFeatureDetails.reduce<Record<string, typeof allFeatureDetails>>((acc, f) => {
    (acc[f.category] ??= []).push(f);
    return acc;
  }, {});
  const categoryOrder = ["core", "alerts", "reports", "support"];
  const sortedCategories = [
    ...categoryOrder.filter((c) => featuresByCategory[c]),
    ...Object.keys(featuresByCategory).filter((c) => !categoryOrder.includes(c)),
  ];
  const hasFeatures = allFeatureDetails.length > 0 || (plan.features?.length ?? 0) > 0;

  // Each plan object carries its own amount/interval/id at the top level.
  // pkg.plans[] lists sibling plans for this package — use it only to find
  // an annual variant when the billing toggle is switched to annual.
  const annualPlan = pkg?.plans?.find((p) => p.interval === "year" && p.is_active !== false);
  const activePlanId = isAnnual && annualPlan ? annualPlan.id : plan.id;

  const monthly = parseFloat(plan.amount) || 0;
  // Annual plan amount may be the full-year price (divide by 12) or the monthly-equivalent — detect by scale
  const rawAnnualAmount = annualPlan ? parseFloat(annualPlan.amount) : 0;
  const annualMonthlyEquivalent = rawAnnualAmount > 0
    ? (rawAnnualAmount > monthly * 6 ? rawAnnualAmount / 12 : rawAnnualAmount)
    : monthly * 0.8;
  const display = isAnnual ? annualMonthlyEquivalent : monthly;
  const annualTotal = rawAnnualAmount > 0 ? rawAnnualAmount : monthly * 12 * 0.8;

  const currencyCode = (annualPlan?.currency ?? plan.currency)?.toUpperCase() || "USD";
  const priceFormat: Format = useMemo(
    () => ({
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: currencyCode === "INR" ? 0 : 2,
      maximumFractionDigits: currencyCode === "INR" ? 0 : 2,
    }),
    [currencyCode],
  );

  const annualPriceFormat: Format = useMemo(
    () => ({
      ...priceFormat,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    [priceFormat],
  );

  const isCurrentPlan = plan.is_current_plan;

  const changeLabel: "Upgrade" | "Downgrade" | null =
    !isCurrentPlan && hasActivePlan && !isEnterprise && currentPlanAmount != null
      ? monthly > currentPlanAmount
        ? "Upgrade"
        : "Downgrade"
      : null;
  const isThisPending = isPending && pendingPlanId === activePlanId;

  const scaleLimitLabel = maxApps == null ? "Unlimited" : String(maxApps);

  const pullLabel =
    initialDataPullDays != null
      ? formatDays(initialDataPullDays)
      : null;
  const windowLabel =
    liveDashboardWindowDays != null
      ? formatDays(liveDashboardWindowDays)
      : null;

  const hasDataParams = pullLabel !== null || windowLabel !== null;
  const hasScaleLimit = maxApps !== undefined;

  /* ── CTA label ─────────────────────────────── */
  const ctaLabel = () => {
    if (isCurrentPlan) return "Your Active Plan";
    if (isEnterprise) return "Contact Sales";
    if (hasActivePlan && !isEnterprise) return "Change Plan";
    if (plan.trial_days > 0 && canStartTrial) return `Start ${plan.trial_days}-Day Trial`;
    return "Subscribe Now";
  };

  /* ── Fine print ─────────────────────────────── */
  const finePrint = () => {
    if (isCurrentPlan) return null;
    if (isEnterprise) return "Reply within 2 business hours";
    if (hasActivePlan && !isEnterprise) return "Upgrades charge immediately · downgrades apply next cycle";
    if (plan.trial_days > 0 && canStartTrial) return `No charge until Day ${plan.trial_days + 1} · cancel anytime`;
    return "Secure payment via Stripe";
  };

  /* ── Token helpers ──────────────────────────── */
  const { isDark } = useTheme();
  const dk = theme.dark || isDark;
  const ink = dk ? "#ffffff" : "#0f172a";
  const ink2 = dk ? "rgba(255,255,255,0.88)" : "#1e293b";
  const muted = dk ? "rgba(255,255,255,0.50)" : "#64748b";
  const sub = dk ? "rgba(255,255,255,0.09)" : "rgba(26,61,124,0.08)";

  /* ── Card container ─────────────────────────── */
  const cardStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: isCurrentPlan ? "19px" : "20px",
    overflow: "hidden",
    background: dk
      ? "linear-gradient(170deg, #1c1c1e 0%, #232323 55%, #282828 100%)"
      : theme.featured
        ? "linear-gradient(176deg, #ffffff 0%, rgba(26,61,124,0.03) 100%)"
        : "#ffffff",
    border: isCurrentPlan
      ? "none"
      : dk
        ? "1.5px solid rgba(255,255,255,0.10)"
        : theme.featured
          ? "2px solid #1a3d7c"
          : "1.5px solid rgba(26,61,124,0.12)",
    boxShadow: theme.featured
      ? "0 0 0 3px rgba(26,61,124,0.12), 0 20px 60px rgba(26,61,124,0.22)"
      : dk
        ? "0 8px 32px rgba(0,0,0,0.40)"
        : "0 3px 16px rgba(26,61,124,0.08)",
  };

  return (
    <>
      <style>{`
        .pc-wrapper { transition: transform .32s cubic-bezier(.4,0,.2,1); }
        .pc-wrapper:hover { transform: translateY(-5px) !important; }
        .pc-wrapper:hover .pc-card { box-shadow: 0 16px 50px rgba(26,61,124,0.15) !important; }
        .pc-wrapper.featured { transform: translateY(-14px) scale(1.02); }
        .pc-wrapper.featured:hover { transform: translateY(-18px) scale(1.02) !important; }
        .pc-wrapper.featured:hover .pc-card { box-shadow: 0 0 0 3px rgba(26,61,124,0.16), 0 28px 70px rgba(26,61,124,0.28) !important; }
        .pc-btn { transition: all .22s cubic-bezier(.4,0,.2,1); }
        .pc-btn:not(:disabled):hover { transform: translateY(-2px); filter: brightness(1.06); }
      `}</style>
      {/* ── Glow wrapper: handles transform + animated border ── */}
      <GradientBorderCard
        active={isCurrentPlan}
        className={cn("pc-wrapper", theme.featured && "featured")}
        style={{ position: "relative", borderRadius: "22px" }}
        glowShadow={
          isCurrentPlan
            ? dk
              ? "0 0 28px rgba(255,255,255,0.07), 0 0 56px rgba(0,0,0,0.30)"
              : "0 0 28px rgba(26,61,124,0.22), 0 0 56px rgba(48,205,115,0.14)"
            : undefined
        }
      >
        <div className="pc-card" style={cardStyle}>
          {/* ── "★ Most Popular" banner ── */}
          {theme.featured && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                background: "linear-gradient(90deg, #1a3d7c, #2a5ba8)",
                color: "#fff",
                fontSize: "9px",
                fontWeight: 700,
                padding: "5px 22px",
                borderRadius: "0 0 12px 12px",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                zIndex: 10,
                boxShadow: "0 4px 18px rgba(26,61,124,0.35)",
              }}
            >
              ★ Most Popular
            </div>
          )}

          {/* ════════════════ CARD BODY ════════════════ */}
          <div
            style={{
              padding: theme.featured ? "44px 24px 0" : "28px 24px 0",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Plan name badge row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  borderRadius: "999px",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  background: theme.badgeBg,
                  color: theme.badgeColor,
                  border: `1px solid ${theme.badgeBorder}`,
                }}
              >
                {plan.name}
              </span>

              {/* Max apps chip — only shown when backend provides it */}
              {hasScaleLimit && !isEnterprise && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "3px 9px",
                    borderRadius: "999px",
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    background: dk
                      ? "rgba(255,255,255,0.07)"
                      : "rgba(26,61,124,0.07)",
                    color: dk ? "rgba(255,255,255,0.46)" : "#64748b",
                    border: dk
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "1px solid rgba(26,61,124,0.18)",
                  }}
                >
                  {scaleLimitLabel === "Unlimited" ? "∞" : scaleLimitLabel}
                  &nbsp;apps
                </span>
              )}
            </div>

            {/* Description */}
            <p
              style={{
                fontSize: "12.5px",
                color: muted,
                lineHeight: 1.58,
                marginBottom: "20px",
                minHeight: "38px",
              }}
            >
              {plan.description || "Professional toolkit for campaign management."}
            </p>

            {/* ── Price ── */}
            <div style={{ marginBottom: "16px" }}>
              {isEnterprise ? (
                <>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "40px",
                      fontWeight: 800,
                      lineHeight: 1,
                      letterSpacing: "-0.04em",
                      color: "#fff",
                      marginBottom: "6px",
                    }}
                  >
                    Custom
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.40)",
                      marginBottom: "10px",
                    }}
                  >
                    Negotiated to your portfolio &amp; team
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "5px 12px",
                      background: "rgba(48,205,115,0.10)",
                      border: "1px solid rgba(48,205,115,0.22)",
                      borderRadius: "8px",
                    }}
                  >
                    <InfinityIcon size={12} color="#30cd73" />
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      Unlimited apps
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* Main price */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "4px",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "40px",
                        fontWeight: 600,
                        lineHeight: 1,
                        letterSpacing: "-0.03em",
                        color: ink,
                      }}
                    >
                      <AnimatedPrice value={display} format={priceFormat} />
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        color: muted,
                        fontWeight: 500,
                        paddingBottom: "6px",
                      }}
                    >
                      /mo
                    </span>
                    {changeLabel && (
                      <button
                        onClick={() => onSubscribe(activePlanId)}
                        style={{ alignSelf: "center", marginLeft: "6px", paddingBottom: "2px" }}
                        className={cn(
                          "inline-flex items-center px-2 py-[3px] rounded-full text-[10px] font-bold tracking-wide border cursor-pointer transition-colors",
                          changeLabel === "Upgrade"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                            : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
                        )}
                      >
                        {changeLabel}
                      </button>
                    )}
                  </div>

                  {/* Annual sub-line */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "18px",
                      marginBottom: "10px",
                    }}
                  >
                    {isAnnual ? (
                      <>
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "12px",
                            color: "#94a3b8",
                            textDecoration: "line-through",
                          }}
                        >
                          <AnimatedPrice value={monthly} format={priceFormat} />
                          /mo
                        </span>
                        <span
                          style={{
                            fontSize: "11.5px",
                            color: "#16a34a",
                            fontWeight: 600,
                          }}
                        >
                          Save 20% ·{" "}
                          <AnimatedPrice
                            value={annualTotal}
                            format={annualPriceFormat}
                          />
                          /yr
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: "11.5px", color: muted }}>
                        Switch to annual — save 20%
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ── Scale limit chip — only shown when backend provides max_apps ── */}
            {hasScaleLimit && (
              <div
                style={{
                  background: theme.scaleBg,
                  border: dk
                    ? "1px solid rgba(255,255,255,0.11)"
                    : `1px solid ${theme.badgeBorder}`,
                  borderRadius: "13px",
                  padding: "12px 15px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.13em",
                      textTransform: "uppercase",
                      color: theme.scaleLabelColor,
                      marginBottom: "2px",
                    }}
                  >
                    Scale limit
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: 800,
                      color: theme.scaleValueColor,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {maxApps == null ? "Unlimited" : `${maxApps} Apps`}
                  </div>
                </div>
                {pkg?.max_team_seats != null && (
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.13em",
                        textTransform: "uppercase",
                        color: theme.scaleLabelColor,
                        marginBottom: "2px",
                      }}
                    >
                      Team seats
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: 800,
                        color: theme.scaleValueColor,
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {pkg.max_team_seats}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Data parameters — only shown when backend provides them ── */}
            {hasDataParams && (
              <div
                style={{
                  background: dk
                    ? "rgba(255,255,255,0.05)"
                    : `linear-gradient(135deg, rgba(26,61,124,${theme.featured ? "0.07" : "0.04"}), rgba(42,91,168,${theme.featured ? "0.05" : "0.03"}))`,
                  border: dk
                    ? "1px solid rgba(48,205,115,0.18)"
                    : `1px solid ${theme.featured ? "#8aadd6" : "rgba(26,61,124,0.16)"}`,
                  borderRadius: "13px",
                  padding: "13px 15px",
                  marginBottom: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "9px",
                }}
              >
                <div
                  style={{
                    fontSize: "9.5px",
                    fontWeight: 700,
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    color: dk ? "rgba(48,205,115,0.80)" : "#2a5ba8",
                  }}
                >
                  Data Parameters
                </div>

                {pullLabel !== null && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11.5px",
                        fontWeight: 500,
                        color: muted,
                      }}
                    >
                      <CloudArrowDownIcon
                        size={12}
                        color={dk ? "#30cd73" : "#1a3d7c"}
                      />
                      Initial Data Pull
                    </div>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        padding: "2px 9px",
                        borderRadius: "6px",
                        background: dk
                          ? "rgba(48,205,115,0.14)"
                          : `rgba(26,61,124,${theme.featured ? "0.12" : "0.08"})`,
                        color: dk
                          ? "#30cd73"
                          : theme.featured
                            ? "#1a3d7c"
                            : "#2a5ba8",
                      }}
                    >
                      {pullLabel}
                    </span>
                  </div>
                )}

                {windowLabel !== null && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11.5px",
                        fontWeight: 500,
                        color: muted,
                      }}
                    >
                      <DatabaseIcon
                        size={12}
                        color={dk ? "#bfcfe8" : "#2a5ba8"}
                      />
                      Live Dashboard Window
                    </div>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        padding: "2px 9px",
                        borderRadius: "6px",
                        background: dk
                          ? "rgba(191,207,232,0.14)"
                          : `rgba(42,91,168,${theme.featured ? "0.11" : "0.08"})`,
                        color: dk
                          ? "#bfcfe8"
                          : theme.featured
                            ? "#1a3d7c"
                            : "#2a5ba8",
                      }}
                    >
                      {windowLabel}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ════════════════ FEATURE LIST ════════════════ */}
          <div
            style={{
              padding: "0 24px 0",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            {hasFeatures && (
              <div
                style={{
                  borderTop: `1px solid ${sub}`,
                  paddingTop: "16px",
                  marginBottom: "18px",
                  flex: 1,
                }}
              >
                {allFeatureDetails.length > 0
                  ? sortedCategories.map((cat) => (
                    <div key={cat} style={{ marginBottom: "14px" }}>
                      {/* Category header */}
                      <div
                        style={{
                          fontSize: "9px",
                          fontWeight: 800,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: dk ? "rgba(48,205,115,0.65)" : "#2a5ba8",
                          marginBottom: "6px",
                          paddingBottom: "4px",
                          borderBottom: `1px solid ${sub}`,
                        }}
                      >
                        {cat}
                      </div>

                      {featuresByCategory[cat].map((feat) => (
                        <div
                          key={feat.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0",
                            fontSize: "12px",
                            lineHeight: 1.4,
                            color: feat.enabled ? ink2 : muted,
                            opacity: feat.enabled ? 1 : 0.5,
                          }}
                        >
                          <span
                            style={{
                              flexShrink: 0,
                              color: feat.enabled
                                ? dk ? "#30cd73" : "#22c55e"
                                : muted,
                              display: "flex",
                            }}
                          >
                            {feat.enabled
                              ? <CheckIcon size={12} weight="bold" />
                              : <XCircleIcon size={12} />}
                          </span>
                          <span style={{
                            flex: 1,
                            textDecoration: feat.enabled ? "none" : "line-through",
                          }}>
                            {feat.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))
                  : plan.features?.map((feat, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "5px 0",
                        fontSize: "12.5px",
                        lineHeight: 1.45,
                        color: ink2,
                        borderBottom: `1px solid ${sub}`,
                      }}
                    >
                      <span style={{ flexShrink: 0, color: dk ? "#30cd73" : "#22c55e", display: "flex" }}>
                        <CheckIcon size={13} weight="bold" />
                      </span>
                      <span style={{ flex: 1 }}>{feat}</span>
                    </div>
                  ))
                }
              </div>
            )}

            {/* ── CTA button ── */}
            <button
              disabled={isCurrentPlan || (isPending && !isThisPending)}
              onClick={() =>
                !isCurrentPlan && !isEnterprise && onSubscribe(activePlanId)
              }
              className="pc-btn"
              style={{
                display: "block",
                width: "100%",
                padding: "13px 0",
                marginBottom: "8px",
                marginTop: hasFeatures ? 0 : "auto",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "14px",
                letterSpacing: "0.01em",
                textAlign: "center",
                cursor: isCurrentPlan ? "default" : "pointer",
                border: "none",
                ...ctaButtonStyle(theme.ctaStyle, isCurrentPlan, dk),
              }}
            >
              {isThisPending ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <SpinnerIcon size={15} className="animate-spin" />
                  Loading…
                </span>
              ) : (
                ctaLabel()
              )}
            </button>

            {/* Fine print */}
            <p
              style={{
                textAlign: "center",
                fontSize: "11px",
                color: dk ? "rgba(255,255,255,0.28)" : "#94a3b8",
                marginBottom: "24px",
              }}
            >
              {finePrint()}
            </p>
          </div>
        </div>
      </GradientBorderCard>
    </>
  );
};

/* ── CTA button style helper ── */
function ctaButtonStyle(
  style: ICardTheme["ctaStyle"],
  isCurrentPlan: boolean,
  dk: boolean,
): React.CSSProperties {
  if (isCurrentPlan)
    return {
      border: dk ? "1px solid rgba(48,205,115,0.40)" : "1px solid #1a3d7c",
      color: dk ? "#30cd73" : "#1a3d7c",
      boxShadow: dk ? "0 6px 22px rgba(48,205,115,0.20)" : "0 6px 22px rgba(34,197,94,0.38)",
      opacity: 0.5,
    };
  switch (style) {
    case "ghost":
      return {
        background: dk ? "rgba(255,255,255,0.07)" : "transparent",
        color: dk ? "rgba(255,255,255,0.80)" : "#1e293b",
        border: dk ? "1.5px solid rgba(255,255,255,0.18)" : "1.5px solid rgba(26,61,124,0.28)",
      };
    case "green":
      return {
        background: "linear-gradient(135deg, #22c55e, #30cd73)",
        color: "#fff",
        boxShadow: "0 6px 22px rgba(48,205,115,0.32)",
      };
    case "dark":
      return {
        background:
          "linear-gradient(135deg, rgba(48,205,115,0.18), rgba(26,61,124,0.15))",
        color: "#fff",
        border: "1.5px solid rgba(48,205,115,0.35)",
      };
    case "primary":
    default:
      return {
        background: "linear-gradient(135deg, #1a3d7c, #2a5ba8)",
        color: "#fff",
        boxShadow: "0 6px 24px rgba(26,61,124,0.30)",
      };
  }
}

export default PricingCard;
